'use client'

import * as React from 'react'
import Link from 'next/link'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { raiseCrudError } from '@open-mercato/ui/backend/utils/serverErrors'
import { useQuery } from '@tanstack/react-query'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type DocumentType = 'CMR' | 'ECMR' | 'CIM' | 'SMGS' | 'MULTIMODAL'
type DocumentStatus = 'DRAFT' | 'ISSUED' | 'SIGNED' | 'DELIVERED' | 'ARCHIVED'

const DOCUMENT_TYPES: DocumentType[] = ['CMR', 'ECMR', 'CIM', 'SMGS', 'MULTIMODAL']
const DOCUMENT_STATUSES: DocumentStatus[] = ['DRAFT', 'ISSUED', 'SIGNED', 'DELIVERED', 'ARCHIVED']

type DocumentRow = {
  id: string
  documentType: DocumentType
  documentNumber: string
  status: DocumentStatus
  legId: string | null
  createdAt: string
}

type DocumentsListResponse = {
  items: DocumentRow[]
  total: number
  totalPages: number
}

export default function DocumentsListPage() {
  const t = useT()
  const scopeVersion = useOrganizationScopeVersion()
  const [sorting, setSorting] = React.useState<SortingState>([{ id: 'createdAt', desc: true }])
  const [page, setPage] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})

  const handleSearchChange = React.useCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, [])

  const handleFiltersApply = React.useCallback((values: FilterValues) => {
    setFilterValues(values)
    setPage(1)
  }, [])

  const handleFiltersClear = React.useCallback(() => {
    setFilterValues({})
    setPage(1)
  }, [])

  const params = React.useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(page))
    p.set('pageSize', '50')
    if (search) p.set('search', search)
    if (typeof filterValues.status === 'string' && filterValues.status) {
      p.set('status', filterValues.status)
    }
    if (typeof filterValues.documentType === 'string' && filterValues.documentType) {
      p.set('documentType', filterValues.documentType)
    }
    if (sorting.length > 0) {
      p.set('sortField', sorting[0].id)
      p.set('sortDir', sorting[0].desc ? 'desc' : 'asc')
    }
    return p.toString()
  }, [page, search, filterValues, sorting])

  const { data, isLoading } = useQuery({
    queryKey: ['freight.documents', params, scopeVersion],
    queryFn: async () => {
      const call = await apiCall<DocumentsListResponse>(`/api/freight/documents?${params}`)
      if (!call.ok) {
        await raiseCrudError(call.response, t('documents.messages.loadError', 'Failed to load documents'))
      }
      return call.result ?? { items: [], total: 0, totalPages: 1 }
    },
  })

  const rows = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const columns = React.useMemo<ColumnDef<DocumentRow>[]>(() => [
    {
      accessorKey: 'documentType',
      header: t('documents.table.columns.documentType', 'Type'),
    },
    {
      accessorKey: 'documentNumber',
      header: t('documents.table.columns.documentNumber', 'Number'),
    },
    {
      accessorKey: 'status',
      header: t('documents.table.columns.status', 'Status'),
    },
    {
      accessorKey: 'legId',
      header: t('documents.table.columns.legId', 'Leg'),
      cell: ({ row }) => row.original.legId ?? '—',
    },
    {
      accessorKey: 'createdAt',
      header: t('documents.table.columns.createdAt', 'Created'),
      cell: ({ row }) => {
        const raw = row.original.createdAt
        if (!raw) return '—'
        return new Date(raw).toLocaleDateString()
      },
    },
  ], [t])

  const statusOptions = React.useMemo(() =>
    DOCUMENT_STATUSES.map((value) => ({ value, label: t(`documents.lookups.status.${value}`, value) })),
    [t],
  )

  const documentTypeOptions = React.useMemo(() =>
    DOCUMENT_TYPES.map((value) => ({ value, label: t(`documents.lookups.documentType.${value}`, value) })),
    [t],
  )

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'status',
      label: t('documents.table.filters.status', 'Status'),
      type: 'select',
      options: statusOptions,
    },
    {
      id: 'documentType',
      label: t('documents.table.filters.documentType', 'Type'),
      type: 'select',
      options: documentTypeOptions,
    },
  ], [t, statusOptions, documentTypeOptions])

  return (
    <Page>
      <PageBody>
        <DataTable
          title={t('documents.pages.list.title', 'Transport Documents')}
          actions={(
            <Button asChild>
              <Link href="/backend/documents/create">
                {t('documents.actions.create', 'Create Document')}
              </Link>
            </Button>
          )}
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={handleSearchChange}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={handleFiltersApply}
          onFiltersClear={handleFiltersClear}
          sortable
          sorting={sorting}
          onSortingChange={setSorting}
          perspective={{ tableId: 'freight.documents.list' }}
          rowActions={(row) => (
            <RowActions items={[
              { id: 'view', label: t('documents.actions.view', 'View'), href: `/backend/documents/${row.id}` },
              { id: 'edit', label: t('documents.actions.edit', 'Edit'), href: `/backend/documents/${row.id}/edit` },
              { id: 'sign', label: t('documents.actions.sign', 'Sign'), href: `/backend/documents/${row.id}/sign` },
            ]} />
          )}
          pagination={{ page, pageSize: 50, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
    </Page>
  )
}
